import { FC, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { SelectionChangedEvent } from 'ag-grid-community';
import {
  ButtonContainer,
  SelectOptionRecipesContainer,
  StyledTableWrapper,
} from '../../CopyOptionRecipe/SelectOptionRecipes/SelectOptionRecipes.styles';
import { H2 } from '../../../../common/Headings/Headings.styles';
import { TableRowWrapper } from '../../../../common/SideBySideTableLayout/SideBySideTableLayout.styles';
import Table from '../../../../components/Table/Table';
import Button from '../../../../components/Button/Button';
import { useSpecsWithUnusedSubSpecs } from '../../../../services/spec/spec.service';
import { DeleteOptionRecipesStep } from '../DeleteOptionRecipes.types';
import { useUnusedSubRecipesForSpec } from '../../../../services/subrecipe/subrecipe.service';
import { SelectOptionRecipesProps } from './SelectOptionRecipes.types';

const SelectOptionRecipes: FC<SelectOptionRecipesProps> = (
  {
    selectedOptionRecipes,
    setSelectedOptionRecipes,
    setStep,
  },
) => {
  const availableGridRef = useRef<AgGridReact>(null);
  const selectedGridRef = useRef<AgGridReact>(null);

  const { data: specsData, isLoading: isLoadingSpecs } = useSpecsWithUnusedSubSpecs(true);

  const [selectedSpecNo, setSelectedSpecNo] = useState<number>();
  const { data: unusedOptionRecipesData, isLoading: isLoadingUnusedSubRecipes } = useUnusedSubRecipesForSpec({
    specNo: selectedSpecNo ?? 0,
  }, selectedSpecNo !== undefined);

  const onRecipeSelectionChanged = (event: SelectionChangedEvent) => {
    setSelectedSpecNo(event.api.getSelectedRows()[0]?.specNo);
    setSelectedOptionRecipes([]);
  };

  const onOptionRecipeSelectionChanged = (event: SelectionChangedEvent) => {
    setSelectedOptionRecipes(event.api.getSelectedRows());
  };

  const onFirstDataRendered = () => {
    const inputElement = document.querySelector(
      'input.ag-input-field-input.ag-text-field-input'
    ) as HTMLInputElement;
    inputElement?.focus();
  };

  return (
    <SelectOptionRecipesContainer>
      <H2 data-testid="SelectOptionsRecipesToDeleteTile">Select option recipes to delete</H2>
      <TableRowWrapper>
        <StyledTableWrapper style={{ width: '20%' }}>
          <Table
            ref={availableGridRef}
            showHeader={true}
            headerTitle="Recipes"
            onFirstDataRendered={onFirstDataRendered}
            columnDefs={[
              {
                headerName: 'Select',
                minWidth: 60,
                maxWidth: 70,
                flex: 1,
                checkboxSelection: true,
                floatingFilter: false,
                sortable: false,
              },
              { headerName: 'Spec', field: 'specNo', minWidth: 40, flex: 0.5, cellRenderer: 'agGroupCellRenderer', filter: 'agTextColumnFilter'},
              { headerName: 'Description', field: 'description', minWidth: 120, flex: 2, resizable: false, tooltipValueGetter: params => params.value },
            ]}
            rowData={specsData?.data ?? []}
            rowSelection="single"
            suppressRowClickSelection={false}
            isSizeColumnsToFit={false}
            onSelectionChanged={onRecipeSelectionChanged}
            returnedRecords={specsData?.data?.length || 0}
            noRowsMessage={isLoadingSpecs ? 'Loading Recipes' : 'No Recipes with unused Option Recipes'}
            height={'100%'}
          />
        </StyledTableWrapper>
        <StyledTableWrapper>
          <Table
            ref={selectedGridRef}
            showHeader={true}
            headerTitle="Option Recipes"
            columnDefs={[
              {
                minWidth: 70,
                field: '',
                headerName: 'Select',
                checkboxSelection: true,
                floatingFilter: false,
                sortable: false,
                flex: 0.5,
                pinned: selectedSpecNo === undefined ? undefined: 'left'
              },
              { headerName: 'Sub-Spec', field: 'subSpecNo', minWidth: 80, maxWidth: 220, flex: 0.5, pinned: selectedSpecNo === undefined ? undefined: 'left' },
              {
                headerName: 'Class',
                field: 'classDescription',
                minWidth: 200,

                flex: 2,
                valueFormatter: params => {
                  return `${params.data.classId} ~ ${params.data.classDescription}`;
                },
              },
              {
                headerName: 'Base',
                field: 'baseDescription',
                minWidth: 200,

                flex: 2,
                valueFormatter: params => {
                  return `${params.data.baseId} ~ ${params.data.baseDescription}`;
                },
              },
              {
                headerName: 'Size',
                field: 'sizeDescription',
                minWidth: 200,

                flex: 2,
                valueFormatter: params => {
                  return `${params.data.sizeId} ~ ${params.data.sizeDescription}`;
                },
              },
              {
                headerName: 'Product',
                field: 'productDescription',
                minWidth: 200,

                flex: 2,
                valueFormatter: params => {
                  return `${params.data.productId} ~ ${params.data.productDescription}`;
                },
              },
              {
                headerName: 'Option',
                field: 'optionDescription',
                minWidth: 200,

                flex: 2,
                valueFormatter: params => {
                  return `${params.data.optionId} ~ ${params.data.optionDescription}`;
                },
              },
              { headerName: 'Spec', field: 'specNo', minWidth: 120,  flex: 0.5 },
            ]}
            rowData={unusedOptionRecipesData?.data ?? []}
            rowSelection="multiple"
            suppressRowClickSelection={false}
            suppressMovableColumns={true}
            rowMultiSelectWithClick={true}
            isSizeColumnsToFit={false}
            onSelectionChanged={onOptionRecipeSelectionChanged}
            returnedRecords={unusedOptionRecipesData?.data.length ?? 0}
            noRowsMessage={isLoadingUnusedSubRecipes ? 'Loading Option Recipes' : 'Please choose a spec to load'}
            height={'100%'}
          />
        </StyledTableWrapper>
      </TableRowWrapper>
      <ButtonContainer>
        <Button label="Next" onClick={() => setStep(DeleteOptionRecipesStep.ReviewChanges)}
                disabled={selectedOptionRecipes.length === 0} />
      </ButtonContainer>
    </SelectOptionRecipesContainer>
  );
};

export default SelectOptionRecipes;